import { captureVisitorConsent } from "@orval/ageless-visitor-consent/ageless-visitor-consent";
import { ConsentBannerProvider, DataPrivacyRegion, VisitorConsentSource } from "@orval/model";

import { getErrorStatus, isClientErrorStatus } from "@/core/api/http-error.utils";
import { getOrCreateVisitorId } from "@/core/identity/visitorId";
import { logger } from "@/core/logging";
import { localStorageService } from "@/core/storage";

import type {
  BannerPolicy,
  ConsentProvenance,
  ConsentStatus,
  ResolvedConsentStatus,
} from "@/core/consent/domain/consent.types";

/**
 * The last decision this browser successfully recorded, persisted (not held in
 * memory) because the dedup has to survive a page load: every load replays the
 * stored decision through the engine, and an in-memory record would still
 * write once per page view.
 *
 * A NEW key, separate from the legacy `ageless.consentRecorded` fingerprint,
 * under this contract: exactly ONE recorder observes the engine per page.
 * The pre-flip legacy recorder's `ageless.consentRecorded` fingerprint still
 * sits in some browsers; it described a different recorder's state and must
 * never suppress this one's first record.
 */
export const CONSENT_RECORD_FINGERPRINT_STORAGE_KEY = "ageless.instrumentation.consentRecorded";

/**
 * The last decision the server DETERMINISTICALLY rejected — see
 * `isDeterministicRejection` — persisted for the same page-load reason as the
 * recorded fingerprint. Re-POSTing a decision the server has already refused
 * would fail identically on every page load; the marker turns that into one
 * loud log per decision. A DIFFERENT decision has a different fingerprint and
 * records normally: the marker suppresses a doomed retry, never a new
 * decision.
 */
export const CONSENT_RECORD_REJECTED_STORAGE_KEY = "ageless.instrumentation.consentRecordRejected";

/** Bump when the fingerprint shape changes, so stale records re-send rather than suppress. */
const FINGERPRINT_VERSION = 1;

/**
 * Identity of a recorded decision: who decided, what is in EFFECT, whether GPC
 * forced it, and which notice generation it was made under. Built from the
 * effective state, not the visitor's answer, because the effective state is
 * what gets recorded — a GPC clamp on top of a grant is a real consent event
 * (source Gpc), and the grant coming back when GPC is switched off is another.
 * The notice version comes from the STATE rather than the environment — the
 * engine already carries it — so a notice bump re-sends each visitor's
 * otherwise unchanged decision exactly once. Banner/Override are absent on
 * purpose: they are the same decision channel distinguished only by history,
 * so folding them in would re-record an unchanged answer purely because the
 * label flipped. GPC stays in: a browser-forced opt-out and a clicked decline
 * with identical values are different decisions. GPC-ness is read from the
 * effective PROVENANCE — the same fact that becomes the recorded `source` —
 * so the fingerprint and the record can never disagree about what was sent.
 * A banner policy's default is marked too: the visitor's answer matching it is
 * their own act and must be recorded. The mark is appended only for a default,
 * so every answer already recorded keeps its fingerprint and nothing re-sends.
 */
const buildFingerprint = (visitorId: string, status: ResolvedConsentStatus): string =>
  [
    `v${FINGERPRINT_VERSION}`,
    status.noticeVersion,
    visitorId,
    `gpc:${status.provenance === "gpc"}`,
    status.decision.analytics,
    status.decision.advertising,
    status.decision.dataSharing,
    status.decision.functional,
    ...(status.provenance === "policyDefault" ? ["policyDefault"] : []),
  ].join("|");

const PROVENANCE_TO_SOURCE: Record<
  ConsentProvenance,
  (typeof VisitorConsentSource)[keyof typeof VisitorConsentSource]
> = {
  banner: VisitorConsentSource.Banner,
  override: VisitorConsentSource.Override,
  gpc: VisitorConsentSource.Gpc,
  policyDefault: VisitorConsentSource.PolicyDefault,
};

const BANNER_PROVIDER_TO_REQUEST: Record<
  BannerPolicy["provider"],
  (typeof ConsentBannerProvider)[keyof typeof ConsentBannerProvider]
> = {
  hubspot: ConsentBannerProvider.HubSpot,
};

/**
 * The policy the answer was given under, as the capture request's three
 * fields: all present or all absent, which is what the server accepts.
 */
const toBannerPolicyRequestFields = (policy: BannerPolicy | null) =>
  policy === null
    ? {}
    : {
        bannerPolicyProvider: BANNER_PROVIDER_TO_REQUEST[policy.provider],
        bannerPolicyId: policy.id,
        bannerPolicyMode: policy.mode,
      };

/**
 * Whether a failed write would fail the same way if retried unchanged. A 4xx
 * is the server saying the REQUEST is wrong — except 408 (the server gave up
 * waiting) and 429 (rate-limited), which are about timing and clear on their
 * own. Everything else — 5xx, a network failure, an abort — is transient: the
 * fingerprint stays unstamped and the next page load retries.
 */
const isDeterministicRejection = (status: number | undefined): status is number =>
  isClientErrorStatus(status) && status !== 408 && status !== 429;

/**
 * A string persisted under one storage key, read lazily once and cached for
 * the life of the sink so a page view costs one storage read per marker,
 * not one per consent change.
 */
const createPersistedMarker = (
  key: string,
): { get: () => string | null; set: (v: string) => void } => {
  let value: string | null = null;
  let hasRead = false;

  return {
    get: () => {
      if (!hasRead) {
        hasRead = true;
        value = localStorageService.getItemSync<string>(key);
      }
      return value;
    },
    set: (next) => {
      value = next;
      hasRead = true;
      localStorageService.setItemSync(key, next);
    },
  };
};

export type ConsentDecisionRecorderOptions = {
  /**
   * The language the notice was PRESENTED in — the banner's language, which
   * `consent.stack` resolves from the route locale through `banner.ts` (the
   * route's for c15t, English for HubSpot).
   *
   * A getter is accepted so a mount whose notice language can change at
   * runtime (a language switch mid-session) reports the language current at
   * the moment each decision is recorded, without restarting the engine —
   * and every vendor behind the bus — on a change.
   */
  readonly consentLanguageCode: string | (() => string);
};

const resolveLanguageCode = (option: string | (() => string)): string =>
  typeof option === "function" ? option() : option;

/**
 * An engine observer. Resolves once every recording queued so far — this
 * status's included — has settled; the return exists for tests and awaiting
 * callers only, the engine's `subscribe` ignores it.
 */
export type ConsentDecisionRecorder = (status: ConsentStatus) => Promise<void>;

/**
 * Records resolved consent decisions — including declines — against our own
 * first-party endpoint, so a decision can be proved from our records rather
 * than a vendor's. An observer on the ENGINE rather than a subscriber on the
 * bus BECAUSE it must see declines: consent evidence is the domain's own
 * concern, and gating it on any decidable scope would mean the one decision we
 * most need evidence of is the one we could never record.
 *
 * No production-domain guard: production records like every other
 * environment, and the capture endpoint is open everywhere (T-3800).
 *
 * Never throws and never blocks: the decision already governs this browser
 * whatever the endpoint does. A transient failure is logged and leaves the
 * recorded fingerprint untouched, so the next page load retries rather than
 * silently dropping the decision; a deterministic rejection is logged once
 * and marked so it is not re-POSTed on every load.
 */
export const createConsentDecisionRecorder = ({
  consentLanguageCode,
}: ConsentDecisionRecorderOptions): ConsentDecisionRecorder => {
  const recorded = createPersistedMarker(CONSENT_RECORD_FINGERPRINT_STORAGE_KEY);
  const rejected = createPersistedMarker(CONSENT_RECORD_REJECTED_STORAGE_KEY);
  // Recordings are SERIALIZED in emission order: a revoke reported while the
  // preceding grant's POST is still pending must reach the server after it,
  // or reverse completion would leave the stale grant as the latest consent
  // event — evidence of tracking granted after the visitor withdrew it.
  // Serialization also closes the duplicate-while-in-flight window, since the
  // fingerprint check runs only after the predecessor has settled.
  let recordQueue: Promise<void> = Promise.resolve();

  const record = async (status: ResolvedConsentStatus, clientReportedAt: string): Promise<void> => {
    // Everything — the identity and storage lookups included — sits inside
    // the try: a throw from any of it must be logged as this sink's own
    // failure, never escape into the queue.
    let fingerprint: string | null = null;
    try {
      const visitorId = getOrCreateVisitorId();
      if (visitorId === null) {
        // Server render: no browser identity to record a decision against.
        return;
      }

      fingerprint = buildFingerprint(visitorId, status);
      // A restored, unchanged decision rebuilds the fingerprint persisted
      // when it was first recorded, which is what keeps a reload from
      // manufacturing evidence of a decision event that never happened.
      if (fingerprint === recorded.get() || fingerprint === rejected.get()) {
        return;
      }

      await captureVisitorConsent({
        visitorId,
        // No platformId: the decision is made above every platform-scoped
        // tree, and a resolved-later guess would attribute it to a practice
        // the visitor had not reached.
        analyticsGranted: status.decision.analytics,
        advertisingGranted: status.decision.advertising,
        dataSharingGranted: status.decision.dataSharing,
        functionalGranted: status.decision.functional,
        // The generation THIS bundle presented, taken from engine state rather than re-read at
        // send time: the server refuses an id it does not publish instead of stamping its own,
        // so a page held open across a notice deploy still records the wording it showed.
        noticeVersion: status.noticeVersion,
        consentLanguageCode: resolveLanguageCode(consentLanguageCode),
        // The server ignores this and records the region CloudFront placed the
        // visitor in (T-4017); an anonymous client's claim about which rules
        // apply to it is not evidence. `NotSensitive` — the explicit "no
        // regional claim" value — is sent only so the wire contract holds.
        dataPrivacyRegion: DataPrivacyRegion.NotSensitive,
        // Maps straight from engine provenance — the engine already encodes
        // "GPC wins over both" and banner-vs-override history, so re-deriving
        // any of it here would be a second, divergent source of truth.
        source: PROVENANCE_TO_SOURCE[status.provenance],
        // Which banner policy the answer was given under. Deliberately NOT in
        // the fingerprint: the policy is evidence about a decision, not a
        // decision, so an unchanged answer seen under another policy re-sends
        // nothing (T-4254).
        ...toBannerPolicyRequestFields(status.answerBannerPolicy),
        // The client's clock at the moment the decision was REPORTED, not the
        // moment its (queued) request went out — recorded beside the server's
        // own stamp and never authoritative.
        clientReportedAt,
      });

      recorded.set(fingerprint);
    } catch (error) {
      const httpStatus = getErrorStatus(error);
      if (fingerprint !== null && isDeterministicRejection(httpStatus)) {
        // The status alone, never the payload: the decision is the visitor's
        // and the log line is not a second place to keep it.
        rejected.set(fingerprint);
        logger.error("ageless_instrumentation_consent_record_rejected", { status: httpStatus });
        return;
      }

      // Visible, never swallowed, never surfaced to the visitor: their
      // decision still applies locally.
      logger.error("ageless_instrumentation_consent_record_failed", undefined, error as Error);
    }
  };

  return (status) => {
    if (status.status !== "resolved") {
      return recordQueue;
    }

    const clientReportedAt = new Date().toISOString();
    recordQueue = recordQueue
      .then(() => record(status, clientReportedAt))
      // record() catches its own failures; this is the guarantee that
      // nothing — a bug in this file included — can reject the chain and
      // silently skip every recording queued after it.
      .catch((error: unknown) => {
        logger.error("ageless_instrumentation_consent_record_failed", undefined, error as Error);
      });
    return recordQueue;
  };
};
